import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneUpdateStatus } from 'src/lib/enum';
import { caloTheme } from 'src/theme/calo';

import { Auth } from '@aws-amplify/auth';
import LogoutIcon from '@mui/icons-material/Logout';
import { Alert, AppBar, Box, IconButton, Toolbar, Tooltip, Typography } from '@mui/material';

import { useAuthStore } from '../lib/store/authStore';
import { clearLoginMethod, getRandomIntercomId } from '../lib/utils';
import AddPhoneModal from './AddPhoneModal';
import Icon from './Icon';

interface TopBarProps {
  affiliateId?: string;
  onPhoneAdded?: (phoneNumber?: string) => void;
  phoneUpdateStatus?: PhoneUpdateStatus;
  currentPhoneNumber?: string;
}

const TopBar = ({ affiliateId, onPhoneAdded, phoneUpdateStatus, currentPhoneNumber }: TopBarProps) => {
  const showProfileBanner = phoneUpdateStatus && phoneUpdateStatus !== PhoneUpdateStatus.COMPLETED;
  const { userId, setUserId } = useAuthStore();
  const naviagte = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const signOut = async () => {
    await Auth.signOut();
    if (window.Intercom) {
      const guestId = getRandomIntercomId();
      window.Intercom('update', {
        id: null,
        user_id: null,
        name: null,
        email: null,
        phone: null,
        anonymous_id: guestId,
        page_title: 'calo-affiliates-portal'
      });
    }
    clearLoginMethod();
    setUserId();
    naviagte('/');
  };

  const handleAddNow = () => {
    setShowModal(true);
  };

  const handlePhoneAdded = (phoneNumber?: string) => {
    setShowModal(false);
    onPhoneAdded?.(phoneNumber);
  };

  return (
    <>
      <Box sx={{ flexGrow: 1, marginBottom: 1 }}>
        <AppBar
          position="static"
          sx={{
            background: caloTheme.palette.white,
            height: showProfileBanner ? 'auto' : '64px',
            boxShadow: 'none',
            borderBottom: `1px solid ${caloTheme.palette.neutral100}`
          }}
        >
          <Toolbar sx={{ padding: '0 !important', minHeight: '64px' }}>
            <Icon name="calo" width={128} height={80} />
            <Box sx={{ flexGrow: 1 }} />

            {showProfileBanner && (
              <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                <Tooltip
                  title={phoneUpdateStatus === PhoneUpdateStatus.IN_PROGRESS
                    ? 'Click if you want to modify the phone number again'
                    : 'Add now.'}
                  arrow
                  placement="bottom"
                >
                  <Alert
                    severity={phoneUpdateStatus === PhoneUpdateStatus.IN_PROGRESS ? 'success' : 'info'}
                    onClick={handleAddNow}
                    sx={{
                      borderRadius: '6px',
                      py: 0.5,
                      px: 1.5,
                      minHeight: '32px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {phoneUpdateStatus === PhoneUpdateStatus.IN_PROGRESS
                        ? 'Please re-log in using your phone number to verify it.'
                        : 'Add your phone number to complete your profile and unlock payouts.'}
                    </Typography>
                  </Alert>
                </Tooltip>
              </Box>
            )}

            {userId && (
              <IconButton
                onClick={signOut}
                sx={{
                  borderRadius: '8px',
                  marginRight: '16px',
                  '&:hover': {
                    backgroundColor: caloTheme.palette.secondaryRed50,
                    color: caloTheme.palette.secondaryRed500
                  },
                  [caloTheme.breakpoints.down('sm')]: {
                    marginRight: 0
                  }
                }}
              >
                <LogoutIcon />
              </IconButton>
            )}
          </Toolbar>

        </AppBar>
      </Box>

      <AddPhoneModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onPhoneAdded={handlePhoneAdded}
        affiliateId={affiliateId || ''}
        initialPhoneNumber={phoneUpdateStatus === PhoneUpdateStatus.IN_PROGRESS ? currentPhoneNumber : undefined}
      />
    </>
  );
};

export default TopBar;
